import React from "react";

import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { cx } from "class-variance-authority";
import { motion } from "motion/react";

import { PendoId, PendoProps } from "../../utils/pendo";
import { Button } from "../Button";
import { Tooltip } from "../Tooltip";

import * as theme from "./FilterBarRow.css";

export interface FilterBarRowException {
    /** The exception's operator, shown as a fixed label since it is derived from the row's mode. */
    modeLabel: string;
    /** The exception's value editor. */
    children: React.ReactNode;
}

export interface FilterBarRowProps extends PendoProps {
    removeButtonPendoId?: PendoId;
    children: React.ReactNode;
    onFilterRemoveClick?: () => void;
    columnOptions: DefaultOptionType[];
    selectedColumnOption: string;
    onColumnOptionChange: (value: string) => void;
    /** Filter mode dropdown (is/isNot/etc.) - optional */
    modeOptions?: DefaultOptionType[];
    selectedMode?: string;
    onModeChange?: (value: string) => void;
    /** Shows the button that adds an exception, until the row has one. */
    onAddExceptionClick?: () => void;
    addExceptionLabel?: string;
    /** The row's exception. While present the mode dropdown is locked. */
    exception?: FilterBarRowException;
}

export function FilterBarRow({
    children,
    onFilterRemoveClick,
    columnOptions,
    selectedColumnOption,
    onColumnOptionChange,
    modeOptions,
    selectedMode,
    onModeChange,
    onAddExceptionClick,
    addExceptionLabel,
    exception,
    "data-pendo": dataPendo,
    removeButtonPendoId,
}: FilterBarRowProps) {
    const isStatic = columnOptions.length === 0;
    const isModeLocked = exception !== undefined;
    return (
        <motion.div
            data-pendo={dataPendo}
            initial={{ opacity: 0, y: -30, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: 30, height: 0 }}
            transition={{ duration: 0.2 }}
        >
            <div className={theme.filterBarRow}>
                <div>
                    <Select
                        open={isStatic ? false : undefined}
                        showArrow={isStatic ? false : undefined}
                        tabIndex={isStatic ? -1 : undefined}
                        className={cx(
                            theme.selectFilter,
                            isStatic && theme.selectFilterStatic
                        )}
                        options={columnOptions}
                        value={selectedColumnOption}
                        onChange={onColumnOptionChange}
                    />
                </div>

                {modeOptions && onModeChange && (
                    <div>
                        <Select
                            open={isModeLocked ? false : undefined}
                            showArrow={isModeLocked ? false : undefined}
                            tabIndex={isModeLocked ? -1 : undefined}
                            className={cx(
                                theme.modeSelect,
                                isModeLocked && theme.selectFilterStatic
                            )}
                            dropdownMatchSelectWidth={false}
                            options={modeOptions}
                            value={selectedMode}
                            onChange={onModeChange}
                        />
                    </div>
                )}

                <div className={theme.filterComponent}>
                    {children}

                    {exception && (
                        <>
                            <Select
                                open={false}
                                showArrow={false}
                                tabIndex={-1}
                                className={cx(
                                    theme.modeSelect,
                                    theme.selectFilterStatic
                                )}
                                options={[
                                    {
                                        label: exception.modeLabel,
                                        value: exception.modeLabel,
                                    },
                                ]}
                                value={exception.modeLabel}
                            />
                            {exception.children}
                        </>
                    )}

                    {onAddExceptionClick && !exception && (
                        <Tooltip title={addExceptionLabel}>
                            <Button
                                size="icon"
                                aria-label={addExceptionLabel}
                                onClick={onAddExceptionClick}
                                icon={<PlusOutlined />}
                            />
                        </Tooltip>
                    )}

                    {onFilterRemoveClick && (
                        <Button
                            data-pendo={removeButtonPendoId}
                            variant="text"
                            onClick={onFilterRemoveClick}
                        >
                            <CloseOutlined />
                        </Button>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
